import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/apiConfig";
import { evaluateExpression, isEligibleCalcInput, isMoneyCalcInput } from "@/lib/calculatorUtils";
import { CalculatorHint, CalculatorPopup } from "@/components/calculator/CalculatorPopup";

// Global inline-calculator overlay. Listens for `X` or `=` keys while a
// numeric input is focused; opens a popup pre-filled with the input's value,
// evaluates an arithmetic expression (+ - * / . ( )), and writes the result
// back into the input (dispatching a native `input` event so React state
// updates cleanly).
//
// Enabled EVERYWHERE except POS routes. Skips inputs marked `data-no-calc`.
//
// Safety: expression is sanitized to allowed characters only; evaluation
// uses `new Function()` (not `eval`) with the sanitized string.

export default function CalculatorOverlay() {
  const location = useLocation();
  const [state, setState] = useState({ open: false, expr: "", target: null, anchor: null });
  const [hint, setHint] = useState({ show: false, anchor: null });
  const [enabled, setEnabled] = useState(true);
  const exprRef = useRef(null);

  // Fetch the "Inline Calculator" toggle from display settings once on mount.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setEnabled(true); return; }  // login page: nothing to fetch yet
    axios
      .get(`${API}/settings/display`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEnabled(r.data?.inline_calculator !== false))
      .catch(() => setEnabled(true));
  }, [location.pathname]);

  // Enabled routes: everywhere EXCEPT POS/register/checkout screens.
  const disabled = useMemo(() => {
    if (!enabled) return true;
    const p = (location.pathname || "").toLowerCase();
    return p === "/" || p.startsWith("/pos") || p.startsWith("/register") || p.startsWith("/checkout");
  }, [enabled, location.pathname]);

  useEffect(() => {
    if (disabled) return undefined;

    const onFocus = (e) => {
      const el = e.target;
      if (!isEligibleCalcInput(el)) return;
      if (!isMoneyCalcInput(el)) return;
      setHint({ show: true, anchor: el });
    };
    const onBlur = () => setHint({ show: false, anchor: null });

    const onKey = (e) => {
      if (state.open) return; // Handled by popup's own onKeyDown
      const el = document.activeElement;
      if (!isEligibleCalcInput(el)) return;
      if (!isMoneyCalcInput(el)) return;
      if (e.key === "x" || e.key === "X" || e.key === "=") {
        e.preventDefault();
        setState({ open: true, expr: String(el.value || ""), target: el, anchor: el });
        setHint({ show: false, anchor: null });
      }
    };

    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("focusout", onBlur, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("focusout", onBlur, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [disabled, state.open]);

  useEffect(() => {
    if (state.open && exprRef.current) {
      exprRef.current.focus();
      exprRef.current.select();
    }
  }, [state.open]);

  const commit = () => {
    if (!state.target) return setState({ open: false, expr: "", target: null, anchor: null });
    const result = evaluateExpression(state.expr);
    if (result !== null) {
      // Set React-controlled input's value via the native setter so React re-renders.
      const proto = state.target.tagName === "INPUT" ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
      const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
      setter.call(state.target, String(result));
      state.target.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setState({ open: false, expr: "", target: null, anchor: null });
  };
  const cancel = () => setState({ open: false, expr: "", target: null, anchor: null });

  const anchorRect = (state.anchor || hint.anchor)?.getBoundingClientRect?.();

  return (
    <>
      <CalculatorHint show={hint.show && !state.open} anchor={hint.anchor} />
      {state.open && anchorRect && (
        <CalculatorPopup
          anchorRect={anchorRect}
          expr={state.expr}
          setExpr={(v) => setState((s) => ({ ...s, expr: v }))}
          exprRef={exprRef}
          onCommit={commit}
          onCancel={cancel}
        />
      )}
    </>
  );
}
